"use client";

import React, { type CSSProperties } from 'react';
import { Sparkles, Leaf } from 'lucide-react';
import { PageHeroLayout } from '../../components/common';

const CommunityIllustration: React.FC = () => (
  <div className="relative w-full max-w-[480px] aspect-[4/3]">
    {/* Soft circular halo */}
    <div className="absolute inset-x-4 inset-y-2 rounded-full bg-gradient-to-br from-v2-green-100/70 via-v2-green-50 to-transparent blur-2xl" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] aspect-square rounded-full border border-dashed border-v2-green-200" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[62%] aspect-square rounded-full bg-gradient-to-br from-v2-green-50 to-v2-neutral-0 shadow-[0_20px_60px_-30px_rgba(30,140,58,0.25)]" />

    {/* Sparkle accents */}
    <Sparkles size={20} className="absolute top-6 left-10 text-v2-green-300 animate-pulse" />
    <Sparkles size={14} className="absolute top-16 right-14 text-v2-green-400" />
    <Sparkles size={16} className="absolute bottom-16 left-12 text-v2-green-300 animate-pulse" />

    {/* Center leaf badge */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%] w-24 h-24 rounded-full bg-gradient-to-br from-v2-green-500 to-v2-green-700 flex items-center justify-center text-white shadow-[0_18px_30px_-12px_rgba(30,140,58,0.5)]">
      <Leaf size={42} strokeWidth={1.8} />
    </div>

    {/* People silhouettes */}
    <svg
      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-80 h-32 drop-shadow-sm"
      viewBox="0 0 280 120"
      fill="none"
      aria-hidden="true"
    >
      {[
        { x: 50, bodyColor: '#1E8C3A' },
        { x: 110, bodyColor: '#6FCA84' },
        { x: 170, bodyColor: '#3AAD56' },
        { x: 230, bodyColor: '#166B2C' },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={36} r={11} fill="#F4D5B8" stroke="#2E2D28" strokeWidth="1.2" />
          <path d={`M${p.x - 11} 33 Q${p.x} 24 ${p.x + 11} 33`} fill="#2E2D28" />
          <path
            d={`M${p.x - 16} 54 Q${p.x - 16} 50 ${p.x - 11} 48 L${p.x + 11} 48 Q${p.x + 16} 50 ${p.x + 16} 54 L${p.x + 16} 100 L${p.x - 16} 100 Z`}
            fill={p.bodyColor}
          />
        </g>
      ))}
    </svg>
  </div>
);

export interface AboutHeroProps {
  eyebrow?: string;
  title?: string;
  titleHighlight?: string;
  description?: string;
  /**
   * Optional uploaded image URL. When present, replaces the default
   * community illustration on the right side of the hero.
   */
  imageUrl?: string;
  imageAlt?: string;
  /**
   * Layout variant — "left" flips the image to the left column and text to
   * the right. Anything else (default, "right", "center", empty) keeps the
   * original layout (text left, image right).
   */
  layoutVariant?: string;
  /** Optional hero CTAs rendered beneath the description. */
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  containerStyle?: CSSProperties;
}

const DEFAULT_TITLE = "We're building a future where ";
const DEFAULT_HIGHLIGHT = 'sharing replaces owning.';
const DEFAULT_EYEBROW = 'About Rent It Forward';
const DEFAULT_DESCRIPTION =
  "We're building Australia's most trusted community-driven rental marketplace, where sharing creates value for everyone while promoting sustainable living.";

export const AboutHero: React.FC<AboutHeroProps> = ({
  eyebrow,
  title,
  titleHighlight,
  description,
  imageUrl,
  imageAlt,
  layoutVariant,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  containerStyle,
}) => {
  const eb = eyebrow?.trim() || DEFAULT_EYEBROW;
  // When the admin supplies a title, treat the highlight as opt-in — falling
  // back to the default highlight would tack "sharing replaces owning." onto
  // every custom title. Only use defaults when nothing was supplied at all.
  const adminProvidedTitle = !!title?.trim();
  const titleText = title?.trim() || DEFAULT_TITLE;
  const highlight = titleHighlight?.trim() || "";
  const desc = description?.trim() || DEFAULT_DESCRIPTION;

  return (
    <PageHeroLayout
      eyebrow={eb}
      eyebrowIcon={Leaf}
      title={titleText}
      titleHighlight={highlight}
      description={desc}
      primaryCta={
        primaryLabel?.trim() && primaryHref?.trim()
          ? { label: primaryLabel.trim(), href: primaryHref.trim() }
          : undefined
      }
      secondaryCta={
        secondaryLabel?.trim() && secondaryHref?.trim()
          ? { label: secondaryLabel.trim(), href: secondaryHref.trim() }
          : undefined
      }
      illustration={<CommunityIllustration />}
      imageUrl={imageUrl}
      imageAlt={imageAlt}
      layoutVariant={layoutVariant}
      containerStyle={containerStyle}
    />
  );
};
